import { DEFAULT_AUTOMATION, DEFAULT_LIMITS, DEFAULT_VOICE, type AutomationSettings, type BrandVoice, type ID, type SettingsMap } from "@ml/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { settingsApi } from "../../lib/api";
import { IG_ACCOUNTS_OVERVIEW_KEY, useIgAccount } from "../../lib/igAccount";
import { settingsKeys } from "../../lib/workspace";

/** Prefix of every settings scope — invalidating it refreshes all of them. */
export const SETTINGS_KEY = settingsKeys.all;

// Stable reference so TanStack Query only re-selects when the data changes.
function selectSettings(s: SettingsMap) {
  return {
    ...s,
    automation: normalizeAutomation(s.automation),
    voice: { ...DEFAULT_VOICE, ...(s.voice ?? {}) } as BrandVoice,
  };
}

/**
 * Whose settings the portal edits: the selected LinkedIn account (each has its own), or null
 * when none is connected yet (the user-level defaults new accounts start from).
 * `ready` is false while the account list is still loading.
 */
export function useSettingsScope(): { igAccountId: ID | null; ready: boolean } {
  const { current, isLoading } = useIgAccount();
  return { igAccountId: current?.id ?? null, ready: !isLoading };
}

/**
 * GET /api/settings?igAccountId=<selected account>, with core blobs merged over defaults so the UI
 * never sees holes. The key carries the account id, so switching accounts refetches.
 */
export function useSettings() {
  const { igAccountId, ready } = useSettingsScope();
  return useQuery({
    queryKey: settingsKeys.scope(igAccountId),
    queryFn: () => settingsApi.get(igAccountId),
    select: selectSettings,
    enabled: ready,
  });
}

export function normalizeAutomation(a: Partial<AutomationSettings> | undefined): AutomationSettings {
  return {
    ...DEFAULT_AUTOMATION,
    ...(a ?? {}),
    limits: { ...DEFAULT_LIMITS, ...(a?.limits ?? {}) },
  };
}

/**
 * PUT /api/settings/:key?igAccountId=<selected account> — updates that account's cache with the
 * server's (validated, clamped) value.
 */
export function useSaveSetting<V>(key: string) {
  const qc = useQueryClient();
  const { igAccountId } = useSettingsScope();
  return useMutation({
    mutationFn: (value: V) => settingsApi.put<V>(key, value, igAccountId),
    onSuccess: (res) => {
      qc.setQueryData<SettingsMap>(settingsKeys.scope(igAccountId), (old) => (old ? { ...old, [key]: res.value } : old));
      // The all-accounts view shows each account's automation switch.
      if (key === "automation") void qc.invalidateQueries({ queryKey: IG_ACCOUNTS_OVERVIEW_KEY });
    },
  });
}
